/**
 * Who can get into the client portal, read from what the list endpoint sends.
 *
 * The mirror of the API's `crm-clients/lib/portalAccess.ts`, and deliberately a
 * thin one: the counting is done there, over two databases, and this file only
 * names the three states so a screen does not have to spell the rule out again.
 *
 * React-free (AGENTS.md §4), so the rule is testable without rendering anything.
 */

/** How many of a client's contacts are in each state. Sent with every list row. */
export interface PortalAccess {
  /** Contacts holding a portal account. */
  linked: number;
  /** Account-less contacts holding an invite that is unused and still valid. */
  invited: number;
}

/** What a client's people add up to, portal-wise. */
export type PortalState = "linked" | "invited" | "none";

/**
 * The one state a client is in.
 *
 * `linked` outranks `invited`, the same way the API decides it: a company whose
 * accountant is already in the portal has portal access, whatever is still
 * outstanding for the second contact.
 *
 * A row with no `portal` field at all is `none`. The field is added by the list
 * endpoint, so a client fetched any other way simply does not carry it, and
 * "not asked" must not draw as "has access".
 */
export function portalState(access: PortalAccess | null | undefined): PortalState {
  if (!access) return "none";
  if (access.linked > 0) return "linked";
  if (access.invited > 0) return "invited";
  return "none";
}
